import { useSeoMeta } from '@unhead/react';
import { AlertTriangle, TrendingUp, Eye, Sparkles } from 'lucide-react';
import { AppLayout } from '@/components/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { useLiquidityData } from '@/hooks/useLiquidityData';
import { useSubscription } from '@/hooks/useSubscription';
import { UNCERTAINTY_DISCLAIMER } from '@/lib/brief';

const Brief = () => {
  useSeoMeta({
    title: 'Daily Brief — BCH Radar',
    description: 'A concise daily macro-liquidity brief for Bitcoin Cash: what changed, the biggest risk, and what to watch next.',
  });

  const { data, isLoading } = useLiquidityData();
  const { isPro } = useSubscription();

  const date = new Date().toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });

  return (
    <AppLayout>
      <div className="mx-auto max-w-2xl">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
              BCH Radar Brief
            </h1>
            <p className="text-sm text-muted-foreground">{date}</p>
          </div>
          {isPro && (
            <Badge className="gap-1 bg-primary/15 text-primary border-primary/30">
              <Sparkles className="h-3 w-3" /> Advanced
            </Badge>
          )}
        </div>

        {isLoading || !data ? (
          <div className="mt-5 space-y-3">
            <Skeleton className="h-32 w-full rounded-xl" />
            <Skeleton className="h-48 w-full rounded-xl" />
          </div>
        ) : (
          <div className="mt-5 space-y-4">
            {/* Headline scores */}
            <Card>
              <CardContent className="grid grid-cols-3 divide-x divide-border/60 p-0 text-center">
                <Metric
                  label="Global Liquidity"
                  value={data.brief.liquidityScore}
                  emoji={data.brief.liquidityEmoji}
                />
                <Metric
                  label="BCH Setup"
                  value={data.brief.bchScore}
                  emoji={data.brief.bchEmoji}
                />
                <Metric label="Cycle" text={data.brief.cycle} />
              </CardContent>
            </Card>

            {/* What changed */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <TrendingUp className="h-4 w-4 text-primary" /> What changed
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ol className="space-y-2 text-sm">
                  {data.brief.whatChanged.map((c, i) => (
                    <li key={i} className="flex gap-2">
                      <span className="font-mono text-primary">{i + 1}.</span>
                      <span className="text-foreground/90">{c}</span>
                    </li>
                  ))}
                </ol>
              </CardContent>
            </Card>

            {/* Risk */}
            <Card className="border-orange-500/30">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <AlertTriangle className="h-4 w-4 text-orange-400" /> Biggest risk
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-foreground/90">{data.brief.biggestRisk}</p>
              </CardContent>
            </Card>

            {/* Strengthen */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">
                  What would strengthen the signal?
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-foreground/90">{data.brief.strengthen}</p>
              </CardContent>
            </Card>

            {/* Watch next */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Eye className="h-4 w-4 text-chart-2" /> Watch next
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2 font-mono text-sm">
                  {data.brief.watchNext.map((w) => (
                    <span
                      key={w}
                      className="rounded-md bg-secondary px-2.5 py-1 text-secondary-foreground"
                    >
                      {w}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>

            <p className="rounded-lg border border-border/60 bg-secondary/30 p-3 text-xs text-muted-foreground">
              <span className="font-semibold text-foreground/80">Uncertainty: </span>
              {UNCERTAINTY_DISCLAIMER}
            </p>

            <p className="text-center text-[11px] text-muted-foreground">
              Brief length: {data.brief.wordCount} words · Generated from the
              deterministic scoring engine, not a price model.
            </p>
          </div>
        )}
      </div>
    </AppLayout>
  );
};

function Metric({
  label,
  value,
  emoji,
  text,
}: {
  label: string;
  value?: number;
  emoji?: string;
  text?: string;
}) {
  return (
    <div className="min-w-0 p-3 sm:p-4">
      <p className="truncate text-[10px] uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      {text !== undefined ? (
        // Text values (e.g. "Tightening") can be long words — keep them small
        // and allow wrapping so they never overflow the narrow grid column.
        <p className="mt-1 text-sm font-bold leading-tight break-words sm:text-lg">
          {text}
        </p>
      ) : (
        <p className="mt-1 font-mono text-2xl font-bold tabular-nums">
          {value} {emoji}
        </p>
      )}
    </div>
  );
}

export default Brief;
